import { FacebookLogoIcon, GoogleLogoIcon, InstagramLogoIcon, TrashIcon } from '@phosphor-icons/react';
import type { GoogleStatus, MetaStatus } from '@regia/shared';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { toast } from 'sonner';
import { ApiError, errorMessage, http } from '../../lib/api';
import { useReauth } from '../../lib/reauth';
import { Badge, Button, Field, IconButton, Input, ListGroup, Section, Skeleton } from '../../ui';

export function useGoogleStatus() {
  return useQuery({ queryKey: ['google-status'], queryFn: () => http.get<GoogleStatus>('/api/google/status'), staleTime: 60_000 });
}

export function useMetaStatus() {
  return useQuery({ queryKey: ['meta-status'], queryFn: () => http.get<MetaStatus>('/api/meta/status') });
}

const cancelled = (err: unknown) => err instanceof ApiError && err.code === 'reauth_cancelled';

function GoogleCard() {
  const qc = useQueryClient();
  const reauth = useReauth();
  const status = useGoogleStatus();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ clientId: '', clientSecret: '', refreshToken: '' });
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    try {
      await reauth(() => http.put('/api/google/credentials', form));
      toast.success('Google collegato');
      setOpen(false);
      setForm({ clientId: '', clientSecret: '', refreshToken: '' });
      qc.invalidateQueries({ queryKey: ['google-status'] });
      qc.invalidateQueries({ queryKey: ['google-properties'] });
    } catch (err) {
      if (!cancelled(err)) toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    try {
      await reauth(() => http.del('/api/google/credentials'));
      toast.success('Google scollegato');
      qc.invalidateQueries({ queryKey: ['google-status'] });
    } catch (err) {
      if (!cancelled(err)) toast.error(errorMessage(err));
    }
  }

  const s = status.data;
  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-sheet)] bg-sheet p-5">
      <div className="flex items-center gap-2">
        <GoogleLogoIcon size={22} className="text-ink" aria-hidden />
        <span className="t-title flex-1 text-base text-ink">Google</span>
        {status.isLoading ? null : s?.connected ? <Badge tone="info">Collegato</Badge> : <Badge>Non collegato</Badge>}
      </div>
      <p className="text-sm text-ink-2">
        {s?.connected
          ? `Account ${s.email ?? ''}: legge scheda Google, recensioni, Search Console e Analytics.`
          : s?.error
            ? s.error
            : 'Serve per la scheda Google (visualizzazioni, chiamate, recensioni), Search Console e Analytics.'}
      </p>
      {open ? (
        <div className="flex flex-col gap-3">
          <Field label="Client ID">{(id) => <Input id={id} value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })} autoComplete="off" />}</Field>
          <Field label="Client secret">{(id) => <Input id={id} value={form.clientSecret} onChange={(e) => setForm({ ...form, clientSecret: e.target.value })} autoComplete="off" />}</Field>
          <Field label="Refresh token">{(id) => <Input id={id} value={form.refreshToken} onChange={(e) => setForm({ ...form, refreshToken: e.target.value })} autoComplete="off" />}</Field>
          <div className="flex gap-2">
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Annulla
            </Button>
            <Button loading={busy} disabled={!form.clientId || !form.clientSecret || !form.refreshToken} onClick={save}>
              Collega
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
            {s?.connected ? 'Cambia credenziali' : 'Collega con le credenziali'}
          </Button>
          {s?.connected ? (
            <Button variant="ghost" size="sm" onClick={disconnect}>
              Scollega
            </Button>
          ) : null}
        </div>
      )}
    </div>
  );
}

function MetaCard() {
  const qc = useQueryClient();
  const reauth = useReauth();
  const status = useMetaStatus();
  const [params, setParams] = useSearchParams();
  const [token, setToken] = useState('');
  const [app, setApp] = useState({ appId: '', appSecret: '' });
  const [showApp, setShowApp] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    const result = params.get('collegamento');
    if (!result?.startsWith('meta')) return;
    if (result === 'meta-ok') toast.success('Facebook collegato');
    else if (result === 'meta-errore') toast.error('Collegamento a Facebook non riuscito');
    const next = new URLSearchParams(params);
    next.delete('collegamento');
    setParams(next, { replace: true });
    qc.invalidateQueries({ queryKey: ['meta-status'] });
  }, [params, setParams, qc]);

  const run = async (key: string, fn: () => Promise<unknown>, ok: string) => {
    setBusy(key);
    try {
      await reauth(fn);
      toast.success(ok);
      qc.invalidateQueries({ queryKey: ['meta-status'] });
      return true;
    } catch (err) {
      if (!cancelled(err)) toast.error(errorMessage(err));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const s = status.data;
  return (
    <div className="flex flex-col gap-3 rounded-[var(--radius-sheet)] bg-sheet p-5">
      <div className="flex items-center gap-2">
        <FacebookLogoIcon size={22} className="text-ink" aria-hidden />
        <InstagramLogoIcon size={22} className="text-ink" aria-hidden />
        <span className="t-title flex-1 text-base text-ink">Facebook e Instagram</span>
        {s?.pages.length ? <Badge tone="info">{s.pages.length} pagine</Badge> : <Badge>Non collegato</Badge>}
      </div>
      {status.isLoading ? (
        <Skeleton className="h-16 w-full" />
      ) : s?.pages.length ? (
        <ListGroup className="ring-1 ring-line-2">
          {s.pages.map((p) => (
            <li key={p.id} className="flex items-center gap-3 px-3 py-2.5">
              {p.picture ? <img src={p.picture} alt="" className="h-9 w-9 rounded-full object-cover" referrerPolicy="no-referrer" /> : null}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-[0.9375rem] font-medium text-ink">{p.name}</span>
                <span className="block truncate text-sm text-ink-3">{p.instagram ? `Instagram @${p.instagram.username}` : 'Senza Instagram collegato'}</span>
              </span>
              <IconButton
                icon={TrashIcon}
                label={`Scollega ${p.name}`}
                size="sm"
                onClick={() => run('del', () => http.del(`/api/meta/pages/${p.id}`), 'Pagina scollegata')}
              />
            </li>
          ))}
        </ListGroup>
      ) : (
        <p className="text-sm text-ink-2">Collega le pagine Facebook (e gli Instagram collegati) per leggere follower, visualizzazioni e post migliori.</p>
      )}
      <div className="flex flex-wrap gap-2">
        {s?.appConfigured ? (
          <Button size="sm" icon={FacebookLogoIcon} onClick={() => window.location.assign('/api/meta/oauth/start')}>
            Accedi con Facebook
          </Button>
        ) : null}
        <Button variant="ghost" size="sm" onClick={() => setShowApp((v) => !v)}>
          {s?.appConfigured ? 'Impostazioni dell’app Meta' : 'Configura l’app Meta'}
        </Button>
      </div>
      {showApp ? (
        <div className="flex flex-col gap-3 rounded-xl bg-sheet-2 p-3">
          <Field label="App ID">{(id) => <Input id={id} inputMode="numeric" value={app.appId} onChange={(e) => setApp({ ...app, appId: e.target.value })} />}</Field>
          <Field label="App secret">{(id) => <Input id={id} value={app.appSecret} onChange={(e) => setApp({ ...app, appSecret: e.target.value })} autoComplete="off" />}</Field>
          <Button
            size="sm"
            className="self-start"
            loading={busy === 'app'}
            disabled={!app.appId || !app.appSecret}
            onClick={async () => {
              if (await run('app', () => http.put('/api/meta/app', app), 'App Meta salvata')) setShowApp(false);
            }}
          >
            Salva
          </Button>
        </div>
      ) : null}
      <div className="grid grid-cols-[minmax(0,1fr)_auto] items-end gap-2">
        <Field label="Oppure incolla un token di accesso" hint="Token utente o di pagina (da Graph API Explorer)">
          {(id, d) => <Input id={id} value={token} onChange={(e) => setToken(e.target.value)} autoComplete="off" aria-describedby={d} />}
        </Field>
        <Button
          variant="secondary"
          className="mb-6"
          loading={busy === 'token'}
          disabled={token.trim().length < 20}
          onClick={async () => {
            if (await run('token', () => http.put('/api/meta/token', { token: token.trim() }), 'Pagine collegate')) setToken('');
          }}
        >
          Collega
        </Button>
      </div>
    </div>
  );
}

export function IntegrationsSection() {
  return (
    <Section title="Collegamenti">
      <div className="flex flex-col gap-3">
        <MetaCard />
        <GoogleCard />
      </div>
    </Section>
  );
}
